import { forwardRef, useImperativeHandle, useState } from "react"
import { Modal, Box, TextField, Button, Paper, IconButton, Typography, Stack } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import CaptureImage from "../../hooks/Utils/CaptureImage"
import { useApiService } from "../../hooks/Api/useApiService"
import useTranslationSharie from "../../hooks/Language/useTranslation"
import useError from "../../hooks/Error/useError"
import T from "../../hooks/Language/T"

const SupportModal = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    childFunction() {
      handleOpen()
    },
  }))

  const [open, setOpen] = useState(false)
  const [form_data, setFormData] = useState({})
  const [image, setImage] = useState(null)
  const { postData } = useApiService()
  const t = useTranslationSharie()
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const { Error: AlertError, error, success } = useError()

  const handleSupport = async () => {
    if (form_data.subject && form_data.description) {
      if (form_data.description.length > 500) {
        error("Description must be at most 250 characters")
        return
      }

      const sentData = {
        ...form_data,
        skill_name: props.skill_name,
        user_name: localStorage.getItem("user"),
        image: image,
      }

      postData("sharie/postissue", sentData)
        .then((data) => {
          if (data.message === "Failed") {
            error("Failed to send issue")
          } else if (data.message === "Issue Exists") {
            error(data.message)
          } else {
            success("Issue added successfully!")
            setTimeout(() => {
              setFormData({})
              handleClose()
            }, 3000)
          }
        })
        .catch(() => {
          error("Image too large")
        })
    } else {
      error("All fields are required")
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'
    >
      <T>
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 520,
            borderRadius: "0.8rem",
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              py: 3,
              px: 2,
              textAlign: "center",
              position: "relative",
              background: "linear-gradient(135deg, #1976d2 0%, #6a1b9a 100%)",
              color: "text.primary",
            }}
          >
            <Typography variant='h5' fontWeight={700} gutterBottom sx={{ letterSpacing: 0.5 }}>
              {t("Get Support / Report an Issue")}
            </Typography>

            <Typography variant='body2' sx={{ opacity: 0.9, fontWeight: 400 }}>
              {t("We’re here to help with any problem or feedback")}
            </Typography>

            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "text.primary",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {/* Content */}
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label={t("Subject")}
              value={form_data.subject || ""}
              onChange={(e) => setFormData({ ...form_data, subject: e.target.value })}
              margin='normal'
              variant='outlined'
            />
            <TextField
              fullWidth
              label={t("Description")}
              multiline
              rows={4}
              value={form_data.description || ""}
              onChange={(e) => setFormData({ ...form_data, description: e.target.value })}
              margin='normal'
              variant='outlined'
            />

            <Box mt={2}>
              <CaptureImage image={image} setImage={setImage} open={open} />
            </Box>

            <Stack direction='column' spacing={2} mt={3} alignItems='center'>
              <Button
                fullWidth
                variant='contained'
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                  borderRadius: 2,
                  py: 1.2,
                  fontWeight: 600,
                }}
                onClick={handleSupport}
              >
                {t("Send")}
              </Button>
              <AlertError />
            </Stack>
          </Box>
        </Paper>
      </T>
    </Modal>
  )
})

export default SupportModal
